import { useSnackbar } from '@components/snackbar';
import { ChevronDownIcon } from '@sketchnote/icons';
import {
  Button,
  Checkbox,
  FileItem,
  FileUpload,
  LoaderXs,
  Radio,
  userSelectStyles,
  validateEmail,
  validatePhoneNumber,
} from '@sketchnote/shared-components';
import { Emoji } from 'emoji-mart';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { API, endpoints } from 'src/api';
import { DateRangePicker } from 'src/features/kanban/component/Item/DatePicker';
import { CLOSE_SIDEBAR, OPEN_SIDEBAR } from 'src/store/types';

import { FileUploadType, FileUploadTypes, Form, FormField, FormOption } from '../../types/form';
import { FormFooter, FormHeader, FormTimer, LinearRangeResponse, Rating } from './components';

export type FormFieldT = FormField & { response: any; error?: string };

export interface ResponseProps {
  data: Form;
  isPreview?: boolean;
  onSubmit?: (data: FormFieldT[]) => void;
}

export const PreviewForm = ({ data, onSubmit, isPreview = false }: ResponseProps) => {
  const [fields, setFields] = useState<FormFieldT[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formSubmit, setFormSubmit] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [open] = useSnackbar();
  const dispatch = useDispatch();
  const router = useRouter();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const timerEnabled = !!data.timer;

  useEffect(() => {
    if (data.fields) {
      const clonedFields = cloneDeep(data.fields) as FormFieldT[];
      clonedFields.map((field) => {
        if (!field.response) {
          field.response = { type: field.responseType };
        }
      });
      setFields(clonedFields);
    }
  }, [data.fields]);

  useEffect(() => {
    if (data.fields) {
      const clonedFields = cloneDeep(data.fields) as FormFieldT[];
      clonedFields.map((field) => {
        if (!field.response) {
          field.response = { type: field.responseType };
        }
      });
      setFields(clonedFields);
    }
  }, [data.fields]);

  useEffect(() => {
    dispatch({ type: CLOSE_SIDEBAR });
    return () => {
      dispatch({ type: OPEN_SIDEBAR });
    };
  }, []);

  const onChange = (value: any, index: number) => {
    const newObj = update(fields, { [index]: { response: { $merge: { value } } } });
    setFields(newObj);
  };

  const onChangeSelectOption = (data: any, index: number) => {
    const selectedOptions = fields[index].response.value || [];
    let value;

    const isOptionSelected = isCheckboxOptionSelected(data, index);

    if (isOptionSelected) {
      value = selectedOptions.filter((item) => item.optionId !== data.optionId);
    } else {
      value = [...selectedOptions, data];
    }

    const newObj = update(fields, { [index]: { response: { $merge: { value } } } });
    setFields(newObj);
  };

  const isValid = (field: FormFieldT) => {
    if (
      !timerEnabled &&
      field.isRequired &&
      (!field.response.value ||
        (Array.isArray(field.response.value) && !field.response.value.length))
    ) {
      return false;
    } else if (
      field.response.type === 'email' &&
      field.response.value &&
      !validateEmail(field.response.value)
    ) {
      return false;
    } else if (
      field.response.type === 'phone' &&
      field.response.value &&
      !validatePhoneNumber(field.response.value)
    ) {
      return false;
    }
    return true;
  };

  const isValidRequired = (field: FormFieldT) => {
    if (field.isRequired) {
      if (!field.response.value) return false;
      if (
        field.responseType === 'upload' &&
        !(Array.isArray(field.response.value) && field.response.value.length)
      )
        return false;
    }
    return true;
  };

  const onValidateField = (index: number) => {
    const field = fields[index];

    const valid = isValidRequired(field);

    setFields(update(fields, { [index]: { error: { $set: valid ? '' : 'Required' } } }));

    if (field.responseType === 'email' && field.response.value) {
      const isEmailValid = validateEmail(field.response.value);
      setFields(
        update(fields, { [index]: { error: { $set: isEmailValid ? '' : 'Invalid email' } } })
      );
    }
    if (field.responseType === 'phone' && field.response.value) {
      const isPhoneValid = validatePhoneNumber(field.response.value);
      setFields(
        update(fields, { [index]: { error: { $set: isPhoneValid ? '' : 'Invalid phone number' } } })
      );
    }
    if (field.responseType === 'upload' && field.response.value) {
      const areFilesValid = true;
      setFields(
        update(fields, {
          [index]: { error: { $set: areFilesValid ? '' : 'Invalid files selected' } },
        })
      );
    }
  };

  const validateFields = () => {
    fields.map((val, index) => {
      onValidateField(index);
    });
  };
  const isValidUrl = (url?: string) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };
  const handleSubmit = async () => {
    if (data.captchaEnabled && !data.timer) {
      const recaptchaValue = recaptchaRef?.current.getValue();
      if (!recaptchaValue) return setSubmitError('Complete the CAPTCHA above to submit your form');
      else setSubmitError('');
    }
    const fieldsValidMap = fields.map(isValid);
    const valid = fieldsValidMap.every(Boolean);

    if (valid) {
      setSubmitting(true);

      fields;

      try {
        const fileUploadedFields = await uploadFiles(fields);
        onSubmit?.(fileUploadedFields);
        if (data.redirect?.isRedirect && data.redirect.url && isValidUrl(data.redirect.url)) {
          router.push(data.redirect.url);
        } else setFormSubmit(true);
      } catch (e) {
        open({
          text: 'Error uploading files',
          duration: 2000,
          position: 'top-center',
          snackType: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    } else {
      validateFields();
    }
  };

  const isOptionImagePresent = (options?: FormOption[]) => {
    return options?.some((item) => item.image);
  };

  function getFileUploadExtensionOptions(fileTypes: FileUploadType[] | null | undefined) {
    if (!fileTypes || !fileTypes.length) return undefined;
    const res: string[] = [];
    fileTypes.forEach((type) => {
      const typeObj = FileUploadTypes[type];
      if (!typeObj) return;
      res.push(...typeObj.extensions);
    });
    return res;
  }

  async function uploadFiles(fields: FormFieldT[]) {
    const newFields: FormFieldT[] = [];
    for (const field of fields) {
      if (field.responseType !== 'upload') newFields.push(field);
      else {
        const newField = field;
        const value: File[] = Array.isArray(field.response.value) ? field.response.value : [];
        const results: { name: string; url: string }[] = [];
        for (const file of value) {
          const data = new FormData();
          data.append('file', file);
          const response = await API.put(endpoints.imageUpload.url, data, {
            headers: { ['Content-Type']: 'multipart/form-data' },
          });
          results.push({
            name: file.name,
            url: response.data,
          });
        }
        newField.response.value = results.length ? results : undefined;
        newFields.push(newField);
      }
    }
    return newFields;
  }

  function handleFileAdd(
    acceptedFiles: File[] | undefined | null,
    rejectedFiles: FileRejection[] | undefined | null,
    fieldIndex: number
  ) {
    if (rejectedFiles?.length) {
      return open({
        text: 'File type not allowed!',
        snackType: 'error',
        position: 'top-center',
        duration: 2000,
      });
    }
    if (acceptedFiles?.length)
      onChange([...(fields[fieldIndex].response.value || []), ...acceptedFiles], fieldIndex);
  }

  function handleRemoveFile(fileIndex: number, fieldIndex: number) {
    const field = fields[fieldIndex];
    if (field.responseType !== 'upload') return;

    const value = field.response.value;
    if (!Array.isArray(value)) return;

    value.splice(fileIndex, 1);
    onChange([...value], fieldIndex);
  }

  const isCheckboxOptionSelected = (item: FormOption, index: number) => {
    const value = fields[index].response.value || [];
    const isChecked = !!value.find((option) => option.optionId === item.optionId);
    return isChecked;
  };

  return (
    <div className="flex flex-col align-bottom h-screen">
      <div className="flex-grow">
        {!!timerEnabled && !formSubmit && (
          <FormTimer timeAllowed={(data.timer || 1) * 60} onTimeEnd={() => handleSubmit()} />
        )}
        <FormHeader coverImage={data.cover} showCover={data.showCover} />

        <div className="py-20 mobile:w-90p tablet:w-84p laptop:w-80p desktop:w-69p  rounded-xl bg-white mx-auto">
          {!formSubmit ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className="flex items-center mb-8">
                {data.icon?.id && (
                  <div className="border border-gray-100 shadow-md bg-gray-199 rounded-sm mr-12 flex items-center justify-center h-40 w-40">
                    {data.icon?.type !== 'image' ? (
                      <Emoji emoji={data.icon?.id} set="apple" size={24} />
                    ) : data.icon?.type === 'image' ? (
                      <img className=" object-center object-cover w-24 h-24" src={data.icon?.id} />
                    ) : (
                      <></>
                    )}
                  </div>
                )}
                <div className="font-semibold bg-white w-full text-lg">{data.title}</div>
              </div>

              <span className="text-gray-999">{data.description}</span>
              <div className="mt-40">
                {fields?.map((field, index) => (
                  <div key={field._id}>
                    <Field
                      title={field.title}
                      description={field.description}
                      key={field._id}
                      image={field?.image}
                      isRequired={field.isRequired && !timerEnabled}
                      showImage={field.showImage}
                    >
                      {field.responseType === 'short-text' && (
                        <input
                          type="text"
                          disabled={isPreview}
                          name={field._id}
                          value={field.response?.value}
                          onChange={(e) => onChange(e.target.value, index)}
                          onBlur={() => onValidateField(index)}
                          placeholder={field.placeholder}
                          className="border  border-gray-200 outline-none rounded-md text-sm pl-12 py-6  text-gray-900 bg-gray-100 w-full"
                        />
                      )}
                      {field.responseType === 'paragraph' && (
                        <textarea
                          name={field._id}
                          disabled={isPreview}
                          value={field.response?.value}
                          onBlur={() => onValidateField(index)}
                          onChange={(e) => onChange(e.target.value, index)}
                          placeholder={field.placeholder}
                          rows={5}
                          className="border h-auto border-gray-200 outline-none rounded-md text-sm pl-12 py-6  text-gray-900 bg-gray-100 w-full"
                          style={{ resize: 'none' }}
                        />
                      )}
                      {field.responseType === 'email' && (
                        <input
                          name={field._id}
                          disabled={isPreview}
                          value={field.response?.value}
                          onChange={(e) => onChange(e.target.value, index)}
                          onBlur={() => onValidateField(index)}
                          placeholder={field.placeholder}
                          className="border  border-gray-200 outline-none rounded-md text-sm pl-12 py-6  text-gray-900 bg-gray-100 w-full"
                        />
                      )}
                      {field.responseType === 'phone' && (
                        <input
                          type="tel"
                          name={field._id}
                          disabled={isPreview}
                          value={field.response?.value}
                          onChange={(e) => onChange(e.target.value, index)}
                          onBlur={() => onValidateField(index)}
                          placeholder={field.placeholder}
                          className="border  border-gray-200 outline-none rounded-md text-sm pl-12 py-6  text-gray-900 bg-gray-100 w-full"
                        />
                      )}

                      {field.responseType === 'linear-scale' && (
                        <LinearRangeResponse
                          data={field}
                          disabled={isPreview}
                          onChange={(value, index) => {
                            onChange(value, index);
                          }}
                        />
                      )}
                      {field.responseType === 'star-rating' && (
                        <Rating
                          higherRange={field.higherRange}
                          disabled={isPreview}
                          onChange={(value) => onChange(value, index)}
                          type={field.ratingStyle}
                        />
                      )}

                      {(field.responseType === 'radio-box' ||
                        field.responseType === 'checkbox') && (
                        <div
                          className={`flex ${
                            isOptionImagePresent(field?.options)
                              ? 'laptop:flex-row mobile:flex-col'
                              : 'flex-col'
                          } flex-wrap justify-between w-full`}
                        >
                          {field.options?.map((item) => {
                            return (
                              <div key={`${item.value} - ${index}`} className="mb-20">
                                {isOptionImagePresent(field?.options) && (
                                  <div
                                    className={`laptop:w-440 laptop:h-221 max-w-440 min-h-48 border ${
                                      item.image
                                        ? 'border-gray-100 p-10'
                                        : 'laptop:border-gray-100 mobile:border-none mobile:hidden laptop:block'
                                    }  rounded-sm`}
                                  >
                                    {item.image ? (
                                      <img
                                        className="object-center rounded-sm max-w-full max-h-full mx-auto"
                                        src={item.image}
                                      />
                                    ) : (
                                      <div className="bg-gray-100 h-full w-full rounded-sm" />
                                    )}
                                  </div>
                                )}
                                <div>
                                  {field.responseType === 'radio-box' ? (
                                    <Radio
                                      className="text-gray-999"
                                      innerClassName=""
                                      disabled={isPreview}
                                      label={item.label}
                                      value={item.value}
                                      name={`${field._id}-radio`}
                                      onChange={() => onChange(item, index)}
                                    />
                                  ) : (
                                    <Checkbox
                                      isDisabled={isPreview}
                                      wrapperClass="text-gray-999 text-lg"
                                      onChange={() => onChangeSelectOption(item, index)}
                                      checked={isCheckboxOptionSelected(item, index)}
                                    >
                                      {item.label}
                                    </Checkbox>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {field.responseType === 'dropdown' && (
                        <Select
                          options={field.options}
                          disabled={isPreview}
                          menuPortalTarget={document.body}
                          onChange={(option) => onChange(option, index)}
                          menuPlacement="bottom"
                          placeholder={field.placeholder}
                          className="mt-14 w-43p"
                          styles={userSelectStyles}
                          components={{
                            DropdownIndicator: () => <ChevronDownIcon className="text-gray-500" />,
                            IndicatorSeparator: () => null,
                          }}
                        />
                      )}
                      {field.responseType === 'date-picker' && (
                        <DateRangePicker
                          readOnly={isPreview}
                          value={field.response.value}
                          onChange={(dateRange) => {
                            onChange(dateRange.length ? dateRange : null, index);
                          }}
                          inputClassName="date-picker bg-gray-100 p-12 w-332 rounded-sm h-48 border border-gray-200 text-left text-base"
                          minDate={field.dateRange?.[0]}
                          maxDate={field.dateRange?.[1]}
                        />
                      )}
                      {field.responseType === 'upload' && (
                        // value length needs to be less than max files allowed to show upload button
                        <>
                          {(!Array.isArray(field.response.value) ||
                            (field.response.value?.length || 0) < (field.maxFilesCount || 1)) && (
                            <>
                              <FileUpload
                                className="mb-16"
                                translations={{
                                  attachments_drop_label: 'Attach files',
                                  attachments_label: 'Attach files ',
                                }}
                                acceptTypes={getFileUploadExtensionOptions(field.fileTypes)}
                                onDrop={(acceptedFiles, rejectedFiles) =>
                                  handleFileAdd(acceptedFiles, rejectedFiles, index)
                                }
                                maxFileSize={
                                  (field.maxFileSize || 2) * 1000 * 1000
                                } /** Converting MBs to Bytes */
                                disabled={isPreview}
                              />
                            </>
                          )}
                          {Array.isArray(field.response.value) &&
                            field.response.value.map((file: File, i: number) => {
                              return (
                                <div className={i !== 0 ? 'mt-12' : ''} key={i}>
                                  <FileItem
                                    fileName={file.name}
                                    fileSize={file.size}
                                    onRemove={() => handleRemoveFile(i, index)}
                                  />
                                </div>
                              );
                            })}
                        </>
                      )}
                      {field.error && <p className="text-red-800 text-sm">{field.error}</p>}
                    </Field>
                  </div>
                ))}
              </div>
              {data.captchaEnabled && !data.timer && (
                <div className="flex justify-center mb-12">
                  <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.GOOGLE_CAPTCHA_SITEKEY} />
                </div>
              )}
              <Button
                size="lg"
                type="submit"
                status={submitting ? 'secondary' : 'primary'}
                className="w-full flex justify-center"
                disabled={isPreview || submitting}
              >
                {submitting ? (
                  <>
                    <span className="mr-12">Attaching your files and submitting the form</span>
                    <LoaderXs className="text-xss" />
                  </>
                ) : (
                  data.submission?.buttonText || 'Submit'
                )}
              </Button>
              {!!submitError && <div className="text-sm text-red-900 mt-8">* {submitError}</div>}
            </form>
          ) : (
            <div className="mx-auto h-full">
              <div className="font-medium shadow-sm p-12 rounded-md">
                {data.submission?.message || 'Thank you for your response'}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="w-full mt-16 mb-16 flex justify-end">
        <FormFooter isPreview={isPreview} />
      </div>
    </div>
  );
};
const Field = ({
  title,
  description,
  children,
  isRequired,
  image,
  showImage,
}: {
  title?: string;
  image?: string;
  description?: string;
  children?: ReactNode;
  isRequired?: boolean;
  showImage?: boolean;
}) => {
  return (
    <div className="rounded-md shadow p-24 border border-gray-200 mb-32">
      {image && showImage && (
        <div className="my-16">
          <img src={image} className="w-full object-center object-cover h-330" />
        </div>
      )}
      <h5 className="mb-8">
        {title} {isRequired && <span className="text-red-900">*</span>}
      </h5>
      <p className="mb-16 text-sm">{description}</p>
      {children}
    </div>
  );
};
